import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface SpecialOrder {
  id: string;
  orderNumber: string;
  orderType: string;
  status: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  totalAmount: number;
  subtotal: number;
  shippingCost: number;
  specialOrderType: string;
  internalNotes: string;
  createdAt: string;
  shippingStreet: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  itemCount: number;
  dealerName: string;
  contactName: string;
  sidemark: string;
  shippingMethod: string;
  parcelCarrier: string;
  parcelServiceName: string;
  parcelTrackingNumber: string;
  parcelShipmentId: string;
  hasLabel: boolean;
  shippingCarrierType: string;
  workOrderId?: string | null;
  contents?: string[];
  items?: any[];
}

const STATUS_COLORS: Record<string, string> = {
  submitted: 'bg-blue-500/20 text-blue-400',
  pending_payment: 'bg-blue-500/20 text-blue-400',
  pending_release: 'bg-blue-500/20 text-blue-400',
  approved: 'bg-green-500/20 text-green-400',
  in_production: 'bg-yellow-500/20 text-yellow-400',
  ready_to_ship: 'bg-purple-500/20 text-purple-400',
  shipped: 'bg-gray-500/20 text-gray-400',
  completed: 'bg-green-500/20 text-green-400',
};

const STATUS_LABELS: Record<string, string> = {
  submitted: 'New',
  pending_payment: 'New',
  pending_release: 'New',
  'submitted,pending_payment,pending_release': 'New',
  approved: 'Approved',
  in_production: 'In Production',
  ready_to_ship: 'Ready to Ship',
  shipped: 'Shipped',
  completed: 'Completed',
};

const TERMINAL_STATUSES = ['shipped', 'completed', 'delivered', 'closed', 'cancelled'];

function formatShippingMethod(value: string | undefined): string {
  if (!value) return 'Not Set';
  const map: Record<string, string> = {
    'willcall': 'Will Call (Pickup)',
    'will_call': 'Will Call (Pickup)',
    'shipping': 'Ship to Customer',
    'ship': 'Ship to Customer',
    'freight': 'Freight Shipping',
    'ltl_freight': 'LTL Freight',
  };
  return map[value.toLowerCase()] || value;
}

function isWillCall(method: string | undefined): boolean {
  return method === 'will_call' || method === 'willcall';
}

export function SpecialOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<SpecialOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [orderItems, setOrderItems] = useState<Record<string, any[]>>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [labelLoading, setLabelLoading] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const response = await fetch(`/api/orders/special-list?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load');
      const data = await response.json();
      // When showing "All", hide completed/cancelled orders so only active work is visible
      const filtered = statusFilter
        ? (data || [])
        : (data || []).filter((o: any) => !TERMINAL_STATUSES.includes(o.status));
      setOrders(filtered);
    } catch (error) {
      console.error('Failed to load special orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async (orderId: string, orderType?: string) => {
    if (orderItems[orderId]) return;
    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = orderType === 'parts'
        ? `/api/orders/${orderId}`
        : `/api/orders/special/${orderId}/items`;
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load items');
      const data = await response.json();
      if (orderType === 'parts' && data.items) {
        setOrderItems(prev => ({ ...prev, [orderId]: data.items.map((item: any) => {
          let partsInfo: any = {};
          if (item.notes) {
            try { partsInfo = typeof item.notes === 'string' ? JSON.parse(item.notes) : item.notes; } catch {}
          }
          const qty = partsInfo.quantity || item.quantity || 1;
          const price = item.unitPrice || item.unit_price || 0;
          const isFabric = partsInfo.fabricType || item.fabricType || item.fabric_type;
          const descParts = isFabric
            ? [
                partsInfo.fabricOrderType === 'panel_replacement' ? 'Panel Replacement' : partsInfo.fabricOrderType === 'fabric_only' ? 'Fabric Only' : null,
                partsInfo.fabricColor || item.fabricColor || item.fabric_color,
                partsInfo.description,
              ]
            : [partsInfo.color, partsInfo.sku ? `SKU: ${partsInfo.sku}` : null, partsInfo.lengthInches ? `${partsInfo.lengthInches}"` : null];
          return {
            assemblyName: partsInfo.name || item.productType || item.product_type || 'Part',
            description: descParts.filter(Boolean).join(', '),
            quantity: qty,
            unitPrice: price,
            lineTotal: (item.totalPrice || item.total_price || price * qty),
            width: partsInfo.width || item.maxWidth || item.max_width || null,
            height: partsInfo.height || item.maxHeight || item.max_height || null,
          };
        }) }));
      } else {
        setOrderItems(prev => ({ ...prev, [orderId]: data }));
      }
    } catch (error) {
      console.error('Failed to load items:', error);
    }
  };

  const toggleExpand = (orderId: string, orderType?: string) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
    } else {
      setExpandedOrder(orderId);
      loadItems(orderId, orderType);
    }
  };

  const updateStatus = async (orderId: string, newStatus: string) => {
    try {
      setActionLoading(orderId);
      const token = localStorage.getItem('auth_token');
      await fetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      await loadOrders();
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const generateLabel = async (orderId: string) => {
    try {
      setLabelLoading(orderId);
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/parcel/shipment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ orderId }),
      });
      const data = await response.json();
      if (data.success) {
        setToast({ message: `UPS label created! Tracking: ${data.trackingNumber}`, type: 'success' });
        await loadOrders();
      } else {
        setToast({ message: data.error || 'Failed to generate label', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to generate label:', error);
      setToast({ message: 'Failed to generate UPS label', type: 'error' });
    } finally {
      setLabelLoading(null);
    }
  };

  const printLabel = async (orderId: string) => {
    try {
      setLabelLoading(orderId);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/parcel/shipment/${orderId}/labels`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success && data.labels?.length > 0) {
        // EVERY label, not just the first. A multi-package shipment returns one
        // label per box, each with its own tracking number — UPS numbers them
        // "1 of 2", "2 of 2". This used to take data.labels[0] and silently drop
        // the rest, so a 2-box shipment could only ever print box 1 and the
        // second parcel had no label.
        // FedEx returns PDF labels; UPS/USPS return GIF images. Rendering a PDF
        // in an <img> shows BLANK, so detect the format (labelFormat field, or
        // the "%PDF" base64 prefix "JVBER") and build the blob with the right
        // MIME type.
        const isPdfLabel = (lbl: any) =>
          String(lbl.labelFormat || '').toUpperCase() === 'PDF' ||
          String(lbl.labelBase64 || '').startsWith('JVBER');
        const toBlobUrl = (lbl: any) => {
          const byteChars = atob(lbl.labelBase64);
          const byteArr = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
          const mime = isPdfLabel(lbl) ? 'application/pdf' : 'image/gif';
          return URL.createObjectURL(new Blob([byteArr], { type: mime }));
        };
        // PDF labels can't go in an <img>. Open each in the browser's native
        // PDF viewer, which renders and prints it correctly.
        if (data.labels.some(isPdfLabel)) {
          data.labels.forEach((lbl: any) => window.open(toBlobUrl(lbl), '_blank'));
          return;
        }
        const blobUrls: string[] = data.labels.map(toBlobUrl);
        const trackingNumbers: string[] = data.labels.map(
          (lbl: any, i: number) => lbl.trackingNumber || data.trackingNumber || `Package ${i + 1}`,
        );
        const blobUrl = blobUrls[0];
        const printWindow = window.open('', '_blank', 'width=816,height=528');
        if (printWindow) {
          printWindow.document.write(`
            <html>
            <head>
              <title>Shipping Label - ${data.trackingNumber}</title>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { background: #f0f0f0; display: flex; justify-content: center; padding: 20px; }
                .page { width: 8.5in; height: 11in; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.2); margin-bottom: 20px; }
                .pkg { font: 600 12px sans-serif; text-align: center; padding-top: 0.15in; color: #333; }
                body { flex-direction: column; align-items: center; }
                .label-area { width: 7.75in; height: 4.75in; margin: 0.375in auto 0 auto; display: flex; justify-content: center; align-items: center; overflow: hidden; }
                img { width: 7.75in; height: 4.75in; object-fit: contain; }
                .toolbar { position: fixed; top: 10px; right: 20px; display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; z-index: 10; }
                button { padding: 10px 20px; font-size: 15px; background: #2563eb; color: #fff; border: none; border-radius: 6px; cursor: pointer; }
                button.all { background: #059669; }
                button:hover { background: #1d4ed8; }
                button:disabled { background: #94a3b8; cursor: not-allowed; }
                @media print {
                  @page { margin: 0; size: letter; }
                  body { background: none; padding: 0; display: block; }
                  .page { box-shadow: none; margin-bottom: 0; page-break-after: always; }
                  .page:last-child { page-break-after: auto; }
                  .label-area { margin: 0.375in auto 0 auto; }
                  img { width: 7.75in; height: 4.75in; object-fit: contain; }
                  button, .toolbar { display: none; }
                }
              </style>
            </head>
            <body>
              <div class="toolbar">
                ${blobUrls.map((_u, i) => `<button onclick="printOne(${i})">Print Label ${i + 1}</button>`).join('')}
                ${blobUrls.length > 1 ? `<button class="all" onclick="printAll()">Print All ${blobUrls.length}</button>` : ''}
              </div>
              ${blobUrls.map((u, i) => `
              <div class="page">
                ${blobUrls.length > 1 ? `<div class="pkg">Package ${i + 1} of ${blobUrls.length} — ${trackingNumbers[i]}</div>` : ''}
                <div class="label-area">
                  <img src="${u}" />
                </div>
              </div>`).join('')}
              <script>
                // Print ONE label at a time — printing all of them together
                // burns a sheet per package and leaves half-used label stock.
                function setVisible(only) {
                  var pages = document.querySelectorAll('.page');
                  for (var i = 0; i < pages.length; i++) {
                    pages[i].style.display = (only === -1 || only === i) ? '' : 'none';
                  }
                }
                function printOne(i) { setVisible(i); window.print(); setVisible(-1); }
                function printAll() { setVisible(-1); window.print(); }
              <\/script>
            </body>
            </html>
          `);
          printWindow.document.close();
        }
      } else {
        setToast({ message: data.error || 'No labels found', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to print label:', error);
      setToast({ message: 'Failed to load label', type: 'error' });
    } finally {
      setLabelLoading(null);
    }
  };

  const statusFilters = ['', 'submitted,pending_payment,pending_release', 'in_production', 'ready_to_ship', 'shipped', 'completed'];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Special Orders</h1>
        <button
          onClick={() => { setLoading(true); loadOrders(); }}
          className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Status Filter */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {statusFilters.map(s => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              statusFilter === s
                ? 'bg-yellow-500 text-black'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {s ? (STATUS_LABELS[s] || s) : 'Active'}
          </button>
        ))}
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-8 text-center">
          <p className="text-gray-400 text-lg">No special orders</p>
          <p className="text-gray-500 text-sm mt-1">Special orders created from BOM calculator will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => (
            <div key={order.id} className="bg-gray-800 rounded-xl overflow-hidden">
              {/* Order Header */}
              <button
                onClick={() => toggleExpand(order.id, order.orderType)}
                className="w-full p-4 text-left"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-yellow-400 font-bold">{order.orderNumber}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      order.orderType === 'parts'
                        ? 'bg-orange-500/20 text-orange-400'
                        : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {order.orderType === 'parts' ? 'PARTS' : 'CUSTOM'}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] || 'bg-gray-600 text-gray-300'}`}>
                      {STATUS_LABELS[order.status] || order.status}
                    </span>
                    {/* Shipping method badge */}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      isWillCall(order.shippingMethod)
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : 'bg-indigo-500/20 text-indigo-400'
                    }`}>
                      {isWillCall(order.shippingMethod) ? 'WILL CALL' : order.parcelCarrier || 'SHIP'}
                    </span>
                  </div>
                  {/* Prices intentionally hidden on the workstation (shop floor). */}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-300">{order.dealerName || order.customerName}</span>
                  <span className="text-gray-500">{new Date(order.createdAt).toLocaleDateString()}</span>
                </div>
                {order.sidemark && (
                  <div className="text-xs text-yellow-500/70 mt-0.5">SM: {order.sidemark}</div>
                )}
                {order.contents && order.contents.length > 0 && (
                  <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-1">
                    {order.contents.map((c, i) => (
                      <span key={i} className="bg-gray-700/60 rounded px-1.5 py-0.5">{c}</span>
                    ))}
                  </div>
                )}
                {order.parcelTrackingNumber && (
                  <div className="text-xs text-green-400 mt-0.5">Tracking: {order.parcelTrackingNumber}</div>
                )}
                {order.shippingCity && !isWillCall(order.shippingMethod) && (
                  <div className="text-xs text-gray-500 mt-1">
                    Ship to: {order.shippingCity}, {order.shippingState} {order.shippingZip}
                  </div>
                )}
              </button>

              {/* Open the work card so the floor can clear it out */}
              {order.workOrderId && (
                <div className="px-4 -mt-2 pb-2">
                  <button
                    onClick={() => navigate(`/work/${order.workOrderId}`)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-yellow-400 hover:text-yellow-300"
                  >
                    Open Work Card
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              )}

              {/* Expanded Details */}
              {expandedOrder === order.id && (
                <div className="border-t border-gray-700 p-4 space-y-3">
                  {/* Shipping Info */}
                  <div className="bg-gray-900 rounded-lg p-3 text-sm space-y-2">
                    <h3 className="text-gray-400 font-medium flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 17h8M8 17l-2 2m2-2l-2-2m8 2l2 2m-2-2l2-2M3 9h18M3 9l3-6h12l3 6M3 9v8a2 2 0 002 2h14a2 2 0 002-2V9" />
                      </svg>
                      Shipping
                    </h3>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Method</span>
                      <span className={isWillCall(order.shippingMethod) ? 'text-cyan-400 font-medium' : 'text-gray-300'}>
                        {formatShippingMethod(order.shippingMethod)}
                      </span>
                    </div>
                    {order.parcelCarrier && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Carrier</span>
                        <span className="text-gray-300">{order.parcelCarrier} — {order.parcelServiceName}</span>
                      </div>
                    )}
                    {order.parcelTrackingNumber && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Tracking</span>
                        <span className="text-green-400 font-mono text-xs">{order.parcelTrackingNumber}</span>
                      </div>
                    )}
                    {order.shippingStreet && !isWillCall(order.shippingMethod) && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Address</span>
                        <span className="text-gray-300 text-right">
                          {order.shippingStreet}<br />
                          {order.shippingCity}, {order.shippingState} {order.shippingZip}
                        </span>
                      </div>
                    )}

                    {/* UPS Label Buttons */}
                    {!isWillCall(order.shippingMethod) && (
                      <div className="flex gap-2 pt-1">
                        {order.hasLabel ? (
                          <button
                            onClick={() => printLabel(order.id)}
                            disabled={labelLoading === order.id}
                            className="flex-1 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-500 disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            {labelLoading === order.id ? 'Loading...' : 'Print Label'}
                          </button>
                        ) : order.parcelCarrier ? (
                          <button
                            onClick={() => generateLabel(order.id)}
                            disabled={labelLoading === order.id}
                            className="flex-1 py-2 bg-amber-600 text-white font-bold rounded-lg hover:bg-amber-500 disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            {labelLoading === order.id ? 'Generating...' : `Generate ${order.parcelCarrier} Label`}
                          </button>
                        ) : null}
                      </div>
                    )}
                  </div>

                  {/* Customer Info */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {order.customerEmail && (
                      <div>
                        <span className="text-gray-500">Email: </span>
                        <span className="text-gray-300">{order.customerEmail}</span>
                      </div>
                    )}
                    {order.customerPhone && (
                      <div>
                        <span className="text-gray-500">Phone: </span>
                        <span className="text-gray-300">{order.customerPhone}</span>
                      </div>
                    )}
                  </div>

                  {/* Pricing hidden on the workstation — labor staff don't see prices. */}

                  {/* Internal Notes */}
                  {order.internalNotes && (
                    <div className="bg-yellow-500/10 rounded-lg p-3 text-sm">
                      <span className="text-yellow-400 font-medium">Notes: </span>
                      <span className="text-gray-300">{order.internalNotes}</span>
                    </div>
                  )}

                  {/* Items */}
                  {orderItems[order.id] ? (
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium text-gray-400">Items</h3>
                      {orderItems[order.id].map((item: any, idx: number) => (
                        <div key={idx} className="bg-gray-900 rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-white font-medium">{item.assemblyName}</span>
                              {item.description && (
                                <p className="text-gray-500 text-xs">{item.description}</p>
                              )}
                              {item.width && item.height && (
                                <p className="text-gray-500 text-xs">{item.width}" x {item.height}"</p>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-gray-500 text-xs">Qty: {item.quantity}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-2">
                      <div className="animate-spin w-5 h-5 border-2 border-yellow-400 border-t-transparent rounded-full mx-auto" />
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    {order.status === 'submitted' && (
                      <button
                        onClick={() => updateStatus(order.id, 'in_production')}
                        disabled={actionLoading === order.id}
                        className="flex-1 py-2 bg-yellow-500 text-black font-bold rounded-lg hover:bg-yellow-400 disabled:opacity-50 text-sm"
                      >
                        {actionLoading === order.id ? 'Updating...' : 'Start Fulfillment'}
                      </button>
                    )}
                    {order.status === 'in_production' && (
                      <>
                        <button
                          onClick={() => updateStatus(order.id, 'submitted')}
                          disabled={actionLoading === order.id}
                          className="py-2 px-3 bg-gray-600 text-white font-bold rounded-lg hover:bg-gray-500 disabled:opacity-50 text-sm"
                        >
                          {actionLoading === order.id ? '...' : 'Restart'}
                        </button>
                        <button
                          onClick={() => updateStatus(order.id, 'ready_to_ship')}
                          disabled={actionLoading === order.id}
                          className="flex-1 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-500 disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          {actionLoading === order.id ? 'Completing...' : 'Complete'}
                        </button>
                      </>
                    )}
                    {order.status === 'ready_to_ship' && (
                      <>
                        <button
                          onClick={() => updateStatus(order.id, 'in_production')}
                          disabled={actionLoading === order.id}
                          className="py-2 px-3 bg-gray-600 text-white font-bold rounded-lg hover:bg-gray-500 disabled:opacity-50 text-sm"
                        >
                          {actionLoading === order.id ? '...' : 'Restart'}
                        </button>
                        {isWillCall(order.shippingMethod) ? (
                          // Will Call: no "shipped" step — it's picked up → completed.
                          <button
                            onClick={() => updateStatus(order.id, 'completed')}
                            disabled={actionLoading === order.id}
                            className="flex-1 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-500 disabled:opacity-50 text-sm"
                          >
                            {actionLoading === order.id ? 'Updating...' : 'Mark Picked Up'}
                          </button>
                        ) : (
                          <button
                            onClick={() => updateStatus(order.id, 'shipped')}
                            disabled={actionLoading === order.id}
                            className="flex-1 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-500 disabled:opacity-50 text-sm"
                          >
                            {actionLoading === order.id ? 'Updating...' : 'Mark Shipped'}
                          </button>
                        )}
                      </>
                    )}
                    {order.status === 'shipped' && (
                      <>
                        <button
                          onClick={() => updateStatus(order.id, 'ready_to_ship')}
                          disabled={actionLoading === order.id}
                          className="py-2 px-3 bg-gray-600 text-white font-bold rounded-lg hover:bg-gray-500 disabled:opacity-50 text-sm"
                        >
                          {actionLoading === order.id ? '...' : 'Back'}
                        </button>
                        <button
                          onClick={() => updateStatus(order.id, 'completed')}
                          disabled={actionLoading === order.id}
                          className="flex-1 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-500 disabled:opacity-50 text-sm"
                        >
                          {actionLoading === order.id ? 'Updating...' : 'Mark Complete'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
